import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES, getCurrencyMetadata, type CurrencyMetadata } from "@cede/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface CurrencyState {
  /** Selected currency code (lowercase, e.g., "usd", "idr") */
  selectedCurrency: string;
  /** Conversion rates from USDT to each currency */
  rates: Record<string, number> | null;
  /** Whether rates are currently loading */
  isLoadingRates: boolean;
  /** Whether the initial currency has been auto-detected */
  hasAutoDetected: boolean;

  // Actions
  setSelectedCurrency: (currency: string) => void;
  setRates: (rates: Record<string, number>) => void;
  setIsLoadingRates: (loading: boolean) => void;
  setHasAutoDetected: (detected: boolean) => void;

  // Computed helpers
  getRate: (currencyCode?: string) => number;
  convertFromUsdt: (usdtValue: number, currencyCode?: string) => number;
  getCurrencyMetadata: (currencyCode?: string) => CurrencyMetadata | undefined;
}

export const useCurrencyStore = create<CurrencyState>()(
  persist(
    (set, get) => ({
      selectedCurrency: DEFAULT_CURRENCY,
      rates: null,
      isLoadingRates: false,
      hasAutoDetected: false,

      setSelectedCurrency: (currency) => {
        const lowerCurrency = currency.toLowerCase();
        // Only set if it's a supported currency
        if (SUPPORTED_CURRENCIES[lowerCurrency]) {
          set({ selectedCurrency: lowerCurrency });
        }
      },

      setRates: (rates) => {
        set({ rates, isLoadingRates: false });
      },

      setIsLoadingRates: (isLoadingRates) => {
        set({ isLoadingRates });
      },

      setHasAutoDetected: (hasAutoDetected) => {
        set({ hasAutoDetected });
      },

      getRate: (currencyCode) => {
        const { selectedCurrency, rates } = get();
        const code = (currencyCode || selectedCurrency).toLowerCase();
        if (!rates) return 1;
        return rates[code] || 1;
      },

      convertFromUsdt: (usdtValue, currencyCode) => {
        const rate = get().getRate(currencyCode);
        return usdtValue * rate;
      },

      getCurrencyMetadata: (currencyCode) => {
        const { selectedCurrency } = get();
        const code = currencyCode || selectedCurrency;
        return getCurrencyMetadata(code);
      },
    }),
    {
      name: "currency-preferences",
      // Only persist the selected currency and auto-detection flag
      partialize: (state) => ({
        selectedCurrency: state.selectedCurrency,
        hasAutoDetected: state.hasAutoDetected,
      }),
    },
  ),
);

/**
 * Selector to check if the user is using USD (no conversion needed)
 */
export const useIsUsdCurrency = () => {
  return useCurrencyStore((state) => state.selectedCurrency === "usd");
};

/**
 * Selector to get the current currency metadata
 */
export const useSelectedCurrencyMetadata = () => {
  return useCurrencyStore((state) => getCurrencyMetadata(state.selectedCurrency));
};
